import React, { useState, useMemo } from 'react';
import CalculatorLayout from '../../components/CalculatorLayout';
import SliderInput from '../../components/SliderInput';

const gcd = (a, b) => b === 0 ? Math.abs(a) : gcd(b, a % b);

const RatioCalculator = () => {
  const [a, setA] = useState(8);
  const [b, setB] = useState(12);
  const [c, setC] = useState(20); // for finding d

  const simplified = useMemo(() => {
    const g = gcd(a, b);
    return { num: a / g, den: b / g };
  }, [a, b]);

  const d = useMemo(() => (a !== 0 ? (c * b) / a : 0), [a, b, c]);
  const decimal = useMemo(() => (b !== 0 ? a / b : 0), [a, b]);
  const percent = useMemo(() => decimal * 100, [decimal]);

  return (
    <CalculatorLayout
      title="Ratio Calculator"
      subtitle="Simplify ratios, find equivalent ratios, and convert to percentages."
      category="Math"
      description={<p>Enter any two numbers to see the simplified ratio (lowest terms), decimal value, and percentage. Use the equivalent ratio section to find the missing term in a proportion <em>a:b = c:d</em>.</p>}
      formulaNote="For a : b = c : d, then a × d = b × c"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-5">
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <SliderInput label="A" value={a} onChange={setA} min={1} max={1000} step={1} />
            </div>
            <div className="font-display text-2xl text-slate-400 mb-3">:</div>
            <div className="flex-1">
              <SliderInput label="B" value={b} onChange={setB} min={1} max={1000} step={1} />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-saffron-50 to-indgreen-50/40 rounded-xl border border-slate-200 p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Simplified Ratio</div>
          <div className="font-display text-4xl font-bold text-bharat-navy display-font mt-1">{simplified.num} : {simplified.den}</div>
          <div className="grid grid-cols-2 gap-3 mt-5">
            <Box label="Decimal" value={decimal.toFixed(4)} tone="saffron" />
            <Box label="Percentage" value={percent.toFixed(2) + '%'} tone="green" />
          </div>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-display font-semibold text-bharat-navy mb-4">Equivalent Ratio</h3>
        <div className="flex flex-wrap items-end gap-3">
          <div className="display-font text-2xl text-bharat-navy">{a} : {b}</div>
          <span className="text-slate-400">=</span>
          <div className="flex-1 min-w-[120px]">
            <SliderInput label="C" value={c} onChange={setC} min={1} max={10000} step={1} />
          </div>
          <span className="text-slate-400">:</span>
          <div className="px-4 py-2 bg-saffron-50 border border-saffron-200 rounded-lg display-font text-2xl font-bold text-saffron-700">
            {d.toFixed(2)}
          </div>
        </div>
      </div>
    </CalculatorLayout>
  );
};

const Box = ({ label, value, tone }) => {
  const c = tone === 'saffron' ? 'text-saffron-700' : tone === 'green' ? 'text-indgreen-700' : 'text-bharat-navy';
  return (
    <div className="bg-white rounded-md p-3 border border-slate-100">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`display-font font-semibold mt-0.5 ${c}`}>{value}</div>
    </div>
  );
};

export default RatioCalculator;
